import immer from 'immer';
import type { ModifierDetails } from '@apollo/client/cache';
import type { AsStoreObject } from '@apollo/client/utilities';
import {
  type ApolloCache,
  readFieldIfReference,
  type Reference,
} from '@frameio/graphql-client';
import type {
  Account,
  Project,
  ProjectConnection,
  Workspace,
} from '@frameio/graphql-client/generated';

const updateProjectNodes = (
  value: Reference | AsStoreObject<ProjectConnection>,
  { toReference, isReference, readField }: ModifierDetails,
  accountId: string,
  newProject: Project
) => {
  const original = readFieldIfReference({
    fieldName: 'projects',
    from: toReference({
      __typename: 'Account',
      id: accountId,
    }),
    isReference,
    readField,
    value,
  });

  if (!original) return value;

  return immer(original, (draft) => {
    draft.nodes = [...draft.nodes, newProject];
  });
};

type IncrementProjectCountForWorkspaceInfoBasicProps = {
  cache: ApolloCache<any>;
  destinationWorkspaceId: string;
  newProject: Project;
  accountId: string;
};

export const incrementProjectCountForWorkspaceInfoBasic = ({
  cache,
  destinationWorkspaceId,
  newProject,
  accountId,
}: IncrementProjectCountForWorkspaceInfoBasicProps) => {
  cache.modify<Workspace>({
    id: cache.identify({ __typename: 'Workspace', id: destinationWorkspaceId }),
    fields: {
      visibleActiveProjectCount(value) {
        if (!value) return 1;
        return value + 1;
      },
      projects(value, details) {
        if (!value) return value;
        return updateProjectNodes(value, details, accountId, newProject);
      },
    },
  });

  cache.modify<Account>({
    id: cache.identify({ __typename: 'Account', id: accountId }),
    fields: {
      projects(value, details) {
        return updateProjectNodes(value, details, accountId, newProject);
      },
    },
  });
};
